import ReactSelect, {
    Props as ReactSelectProps,
    createFilter,
    OptionTypeBase,
    mergeStyles,
} from 'react-select';
import { makeStyles, useTheme } from '@material-ui/styles';
import Label from '../Label';
import FieldError from '../FieldError';
import { Theme } from '../../..';

type SelectProps<OptionType> = ReactSelectProps<OptionType> & {
    label?: string;
    required?: boolean;
    error?: string;
};

const Select = <OptionType extends OptionTypeBase>({
    options = [],
    label,
    required,
    error,
    styles = {},
    ...restSelectProps
}: React.PropsWithChildren<SelectProps<OptionType>>): React.ReactElement => {
    const classes = useStyles();
    const customStyles = useSelectStyles<OptionType>();
    const selectStyles = mergeStyles(customStyles || {}, styles);

    return (
        <label className={classes.container}>
            <ReactSelect<OptionType>
                options={options}
                styles={selectStyles}
                components={{ IndicatorSeparator: () => null }}
                filterOption={createFilter({ ignoreAccents: false })}
                noOptionsMessage={() => 'No results.'}
                {...restSelectProps}
            />
            <div className={classes.meta}>
                {label && <Label label={label} required={required} />}
                {error && <FieldError error={error} />}
            </div>
        </label>
    );
};

const useSelectStyles = <
    OptionType extends OptionTypeBase
>(): SelectProps<OptionType>['styles'] => {
    const theme = useTheme<Theme>();
    return {
        control: (provided, { isFocused, isDisabled }) => ({
            ...provided,
            borderRadius: 6,
            minHeight: 32,
            border: 'none',
            backgroundColor: theme.palette.greys[200],
            ...theme.typography.t61,
            lineHeight: 1,
            boxShadow: isFocused ? `0 0 0 1px ${theme.palette.greys[400]}` : 'none',
            opacity: isDisabled ? '60%' : provided.opacity,
        }),
        placeholder: (provided, { isDisabled }) => ({
            ...provided,
            ...theme.typography.t61,
            marginLeft: 1,
            marginRight: 1,
            color: isDisabled ? theme.palette.greys[400] : theme.palette.greys[600],
        }),
        dropdownIndicator: (provided, { isDisabled }) => ({
            ...provided,
            color: isDisabled ? theme.palette.greys[300] : provided.color,
        }),
        valueContainer: provided => ({
            ...provided,
            padding: '3px 7px',
        }),
        singleValue: (provided, { isDisabled }) => ({
            ...provided,
            color: isDisabled ? theme.palette.greys[400] : theme.palette.greys[600],
            ...theme.typography.t61,
            marginLeft: 1,
            marginRight: 1,
        }),
        input: (provided, { isDisabled }) => ({
            ...provided,
            padding: 0,
            color: isDisabled ? theme.palette.greys[400] : theme.palette.greys[600],
        }),
        menu: provided => ({
            ...provided,
            boxShadow: 'none',
            margin: '4px 0',
            border: `1px solid ${theme.palette.greys[300]}`,
            borderRadius: 6,
            backgroundColor: theme.palette.greys[100],
        }),
        menuList: provided => ({
            ...provided,
            padding: 0,
            maxHeight: 120,
        }),
        option: (provided, { isFocused, isSelected }) => ({
            ...provided,
            padding: '8px 10px',
            borderRadius: 6,
            backgroundColor: isFocused ? theme.palette.greys[200] : 'transparent',
            color: isSelected ? theme.palette.greys[500] : theme.palette.greys[700],
            ...theme.typography.t71,
            lineHeight: 1,

            ':active': {
                backgroundColor: theme.palette.greys[200],
            },
        }),
        noOptionsMessage: provided => ({
            ...provided,
            padding: '12px',
            textAlign: 'initial',
            color: theme.palette.greys[500],
            ...theme.typography.t71,
        }),
    };
};

const useStyles = makeStyles(
    () => ({
        container: {
            display: 'flex',
            flexDirection: 'column-reverse',
        },
        meta: {
            display: 'flex',
            alignItems: 'center',
            marginBottom: 4,
        },
    }),
    { classNamePrefix: 'select' }
);

export default Select;
